import React from 'react';
import { Card, Typography, Badge, Tooltip, Grid, Button, Tag, Avatar } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import {
    WhatsAppOutlined,
    FacebookOutlined,
    MessageOutlined,
    InstagramOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    SyncOutlined,
    StopOutlined,
    RobotOutlined,
    KeyOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const getPlatformConfig = (channelType, t) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return {
                icon: WhatsAppOutlined,
                color: '#25D366',
                name: t('whatsapp')
            };
        case 'messenger':
            return {
                icon: MessageOutlined,
                color: '#1877F2',
                name: t('messenger')
            };
        case 'facebook_page':
            return {
                icon: FacebookOutlined,
                color: '#1877F2',
                name: t('facebook')
            };
        case 'instagram':
            return {
                icon: InstagramOutlined,
                color: '#E4405F',
                name: t('instagram')
            };
        default:
            return {
                icon: MessageOutlined,
                color: '#888',
                name: t('unknown')
            };
    }
};

const getTokenExpiryStatus = (expiresAt, isPermanent, t) => {
    if (isPermanent) {
        return { color: 'success', text: t('permanent_token') };
    }
    if (!expiresAt) {
        return { color: 'default', text: t('unknown') };
    }

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
        return { color: 'error', text: t('expired') };
    }
    if (daysUntilExpiry < 7) {
        return { color: 'warning', text: t('expires_soon') };
    }
    return { color: 'processing', text: t('valid') };
};

const ChannelCard = ({ channel, onAction, deleteLoading, updateLoading }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const { isDarkMode } = useTheme();
    const isMobile = !!screens?.xs;
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const navigate = useNavigate();

    const platform = getPlatformConfig(channel.channel_type, t);
    const PlatformIcon = platform.icon;
    const tokenStatus = getTokenExpiryStatus(channel.token_expires_at, channel.is_permanent_token, t);

    const themeColors = {
        background: isDarkMode ? '#141414' : '#fff',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        cardHover: isDarkMode ? '#1f1f1f' : '#fafafa',
        divider: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)'
    };

    const handleDelete = () => {
        setDeleteModalVisible(true);
    };

    const confirmDelete = () => {
        onAction('delete', channel);
        setDeleteModalVisible(false);
    };

    const handleCardClick = () => {
        navigate(`/channels/${channel.id}`);
    };

    const handleSyncToggle = () => {
        const updatedChannel = {
            is_synchronization_enabled: !channel.is_synchronization_enabled,
        };
        onAction('toggleSync', channel, updatedChannel);
    };

    const cardActions = [
        // Toggle Sync Status
        channel.is_synchronization_enabled ? (
            isMobile ? (
                <Button
                    type="text"
                    icon={<PauseCircleOutlined style={{ color: isDarkMode ? '#d8b732' : '#c7c709' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSyncToggle();
                    }}
                    loading={updateLoading?.[channel.id]}
                />
            ) : (
                <Tooltip title={t('pause_sync')}>
                    <Button
                        type="text"
                        icon={<PauseCircleOutlined style={{ color: isDarkMode ? '#d8b732' : '#c7c709' }} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSyncToggle();
                        }}
                        loading={updateLoading?.[channel.id]}
                    />
                </Tooltip>
            )
        ) : (
            isMobile ? (
                <Button
                    type="text"
                    icon={<PlayCircleOutlined style={{ color: isDarkMode ? '#52c41a' : '#00C853' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSyncToggle();
                    }}
                    loading={updateLoading?.[channel.id]}
                />
            ) : (
                <Tooltip title={t('start_sync')}>
                    <Button
                        type="text"
                        icon={<PlayCircleOutlined style={{ color: isDarkMode ? '#52c41a' : '#00C853' }} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSyncToggle();
                        }}
                        loading={updateLoading?.[channel.id]}
                    />
                </Tooltip>
            )
        ),
        // View Details
        isMobile ? (
            <Button
                type="text"
                icon={<EyeOutlined style={{ color: isDarkMode ? '#177ddc' : '#1890ff' }} />}
                onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick();
                }}
            />
        ) : (
            <Tooltip title={t('view_details')}>
                <Button
                    type="text"
                    icon={<EyeOutlined style={{ color: isDarkMode ? '#177ddc' : '#1890ff' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick();
                    }}
                />
            </Tooltip>
        ),
        // Delete
        isMobile ? (
            <Button
                type="text"
                icon={<DeleteOutlined style={{ color: isDarkMode ? '#d32029' : '#ff4d4f' }} />}
                onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                }}
                loading={deleteLoading?.[channel.id]}
            />
        ) : (
            <Tooltip title={t('delete')}>
                <Button
                    type="text"
                    icon={<DeleteOutlined style={{ color: isDarkMode ? '#d32029' : '#ff4d4f' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                    }}
                    loading={deleteLoading?.[channel.id]}
                />
            </Tooltip>
        ),
    ];

    return (
        <Card
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: isMobile ? '100%' : '260px',
                border: 'none',
                boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.05)',
                background: themeColors.background,
                transition: 'all 0.3s ease',
            }}
            styles={{
                body: {
                    padding: '24px',
                },
                actions: {
                    borderTop: `1px solid ${themeColors.divider}`,
                    background: 'transparent',
                }
            }}
            actions={cardActions}
        >
            {/* Header Section */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', gap: '16px', flex: 1, minWidth: 0 }}>
                    <Badge
                        count={
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                backgroundColor: platform.color,
                                border: `2px solid ${themeColors.background}`,
                            }}>
                                <PlatformIcon style={{ fontSize: 12, color: 'white' }} />
                            </div>
                        }
                        offset={[-2, 40]}
                    >
                        <Avatar
                            size={52}
                            src={channel.picture_url}
                            icon={<PlatformIcon />}
                            style={{
                                backgroundColor: channel.picture_url ? 'transparent' : `${platform.color}15`,
                                color: platform.color,
                                border: 'none',
                            }}
                        />
                    </Badge>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, minWidth: 0 }}>
                        <Text
                            strong
                            style={{
                                fontSize: '18px',
                                color: themeColors.text,
                                lineHeight: '1.2',
                                marginBottom: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            title={channel.name}
                        >
                            {channel.name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            {platform.name} {t('channel')}
                        </Text>
                    </div>
                </div>

                {/* Sync Icon */}
                <div style={{ marginLeft: '12px' }}>
                    {isMobile ? (
                        channel.is_synchronization_enabled ? (
                            <SyncOutlined style={{ fontSize: '16px', color: isDarkMode ? '#49aa19' : '#52c41a' }} />
                        ) : (
                            <StopOutlined style={{ fontSize: '16px', color: isDarkMode ? '#8c8c8c' : '#595959' }} />
                        )
                    ) : (
                        <Tooltip title={channel.is_synchronization_enabled ? t('synced') : t('not_synced')}>
                            {channel.is_synchronization_enabled ? (
                                <SyncOutlined style={{ fontSize: '16px', color: isDarkMode ? '#49aa19' : '#52c41a' }} />
                            ) : (
                                <StopOutlined style={{ fontSize: '16px', color: isDarkMode ? '#8c8c8c' : '#595959' }} />
                            )}
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Agent Section */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: isDarkMode ? '#133018' : '#98FB98',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <RobotOutlined style={{ fontSize: '18px', color: isDarkMode ? '#49aa19' : '#00b96b' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                        <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.2' }}>{t('linked_agent')}</Text>
                        {channel.agent ? (
                            <Text strong style={{ fontSize: '14px', color: themeColors.text }} ellipsis>{channel.agent.name}</Text>
                        ) : (
                            <Text type="secondary" style={{ fontSize: '14px' }}>{t('no_agent')}</Text>
                        )}
                    </div>
                </div>
                {/* Visual edit icon - Action could be added later */}
                {channel.agent && <EditOutlined style={{ color: themeColors.secondaryText, fontSize: '16px' }} />}
            </div>

            <div style={{ height: '1px', background: themeColors.divider, marginBottom: '16px' }} />

            {/* Credential Section */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: isDarkMode ? '#1f1f1f' : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <KeyOutlined style={{ fontSize: '18px', color: isDarkMode ? '#8c8c8c' : '#595959' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                        <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.2' }}>{t('used_credential')}</Text>
                        {channel.credential ? (
                            <Text strong style={{ fontSize: '14px', color: themeColors.text }} ellipsis>{channel.credential.name}</Text>
                        ) : (
                            <Text type="secondary" style={{ fontSize: '14px' }}>-</Text>
                        )}
                    </div>
                </div>
                {/* Visual clip icon - Action could be added later */}
                <EditOutlined style={{ color: themeColors.secondaryText, fontSize: '16px', visibility: 'hidden' }} /> {/* Invisible spacer to align if needed, or remove */}
            </div>

            {/* Delete Channel Modal */}
            <ResponsiveModal
                title={t('delete_channel')}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_delete_this_channel')}</div>
            </ResponsiveModal>
        </Card>
    );
};

export default ChannelCard;
